import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ViewMode} from '../../enums/ViewMode';
import PaginatedCollection from '../PaginatedCollection';
import {IPost} from '../../types/resources/Post';
import PostForm from './PostForm';
import PostCard from './PostCard';
import PostListItem from './PostListItem';
import styled from 'styled-components';
import Tabs from '../Tabs';
import Tab from '../Tab';
import {IOnlineCondolenceDetails} from '../../types/resources/OnlineCondolenceDetails';
import {PostType} from '../../enums/PostType';
import {useSettingsContext} from '../../context/settings/useSettingsContext';
import PostListLtrItem from './PostListLtrItem';
import { ButtonLayout } from '../../enums/ButtonLayout';

type TProps = {
    onlineCondolence: IOnlineCondolenceDetails;
    initialPosts: IPost[];
}

const Description = styled.div`
    max-width: 620px;
`;

const PostCollection = ({ onlineCondolence, initialPosts }: TProps) => {
    const { t } = useTranslation();
    const { buttonLayout, messageViewMode } = useSettingsContext();
    const viewMode = messageViewMode as ViewMode;

    const [posts, setPosts] = useState<IPost[]>(initialPosts);
    const [isFormShown, setIsFormShown] = useState<boolean>(false);

    const renderCard = (post: IPost, index: number) => (
        <PostCard key={index} post={post}/>
    );

    const renderListItem = (post: IPost, index: number) => (
        <PostListItem key={index} post={post}/>
    );

    const renderListLtrItem = (post: IPost, index: number) => (
        <PostListLtrItem key={index} post={post}/>
    );

    const addNewPost = (newPost: IPost) => {
        setPosts(currentPosts => [newPost, ...currentPosts]);
    }

    const showPosts = !onlineCondolence.postsAreHidden && (posts.length > 0);

    const sortedPosts = posts.sort((a, b) => {
        return b.createdOn.getTime() - a.createdOn.getTime();
    });

    const isPostingAvailable =
        (!onlineCondolence.disablePosting)
        && (
            onlineCondolence.enableDefaultMessage
            || onlineCondolence.enablePictureMessage
            || onlineCondolence.enableMusicMessage
            || onlineCondolence.enableVideoMessage
        )
    ;

    return (showPosts || isPostingAvailable) ? (
        <div className={'cr-posts-section'}>
            <div className={'cr-posts-section-top'}>
                <div className="cr-posts-section-top-inner cr-bg-grey-light cr-masked-full cr-masked-inner cr-p-lg"></div>
            </div>
            <div className='cr-bg-grey-light'>
                <div className='cr-py-xl cr-container'>
                    {isPostingAvailable &&
                        <div className={`cr-flex cr-flex-col cr-gap-lg cr-py-xl ${buttonLayout === ButtonLayout.Wide ? 'cr-center' : ''}`}>
                            <div className={`cr-flex cr-gap-lg ${buttonLayout === ButtonLayout.Compact ? 'cr-align-center' : ''}`}>
                                <div className={`cr-title cr-text-title cr-text-alert cr-bold ${buttonLayout === ButtonLayout.Wide ? 'cr-text-center' : ''}`}>
                                    {t('create-post-title')}
                                </div>
                                { buttonLayout === ButtonLayout.Compact &&
                                    <button
                                        className='cr-create-post cr-pill cr-bg-alert cr-text-white cr-text-base'
                                        onClick={() => setIsFormShown(!isFormShown)}
                                    >
                                        {t('create-post')}
                                    </button>
                                }
                            </div>
                            <Description className={`cr-description cr-text-primary cr-mb-lg ${buttonLayout === ButtonLayout.Wide ? 'cr-text-center' : ''}`}>
                                {t('create-post-description')}
                            </Description>
                            {isFormShown
                                ?
                                <Tabs
                                    id={'createPost'}
                                    className={'cr-mb-xl'}
                                    activeItemClasses={'cr-bg-alert cr-border-alert cr-text-white'}
                                    itemClasses={'cr-bg-grey-medium cr-border-grey-medium'}
                                    centerOnMobile={true}
                                >
                                    <Tab label={t('create-default-message-tab')} isEnabled={onlineCondolence.enableDefaultMessage}>
                                        <div className={'cr-create-default-message'}>
                                            <div className={'cr-tab-header cr-text-center'}>
                                                <div className={'cr-tab-title cr-text-lg cr-text-primary cr-mb-sm'}>
                                                    {t('create-default-message-title')}
                                                </div>
                                                <div className={'cr-tab-description cr-text-primary'}>
                                                    {t('create-default-message-text')}
                                                </div>
                                            </div>
                                            <PostForm
                                                onlineCondolence={onlineCondolence}
                                                postType={PostType.Message}
                                                handleFormVisibility={() => setIsFormShown(false)}
                                                onPostCreated={addNewPost}
                                            />
                                        </div>
                                    </Tab>
                                    <Tab label={t('create-picture-message-tab')}
                                         isEnabled={onlineCondolence.enablePictureMessage}>
                                        <div className={'cr-create-picture-message'}>
                                            <div className={'cr-tab-header cr-text-center'}>
                                                <div className={'cr-tab-title cr-text-lg cr-text-primary cr-mb-sm'}>
                                                    {t('create-picture-message-title')}
                                                </div>
                                                <div className={'cr-tab-description cr-text-primary'}>
                                                    {t('create-picture-message-text')}
                                                </div>
                                            </div>
                                            <PostForm
                                                onlineCondolence={onlineCondolence}
                                                postType={PostType.MessageWithPhoto}
                                                handleFormVisibility={() => setIsFormShown(false)}
                                                onPostCreated={addNewPost}
                                            />
                                        </div>
                                    </Tab>
                                    <Tab label={t('create-music-message-tab')}
                                         isEnabled={onlineCondolence.enableMusicMessage}>
                                        <div className={'cr-create-music-message'}>
                                            <div className={'cr-tab-header cr-text-center'}>
                                                <div className={'cr-tab-title cr-text-lg cr-text-primary cr-mb-sm'}>
                                                    {t('create-music-message-title')}
                                                </div>
                                                <div className={'cr-tab-description cr-text-primary'}>
                                                    {t('create-music-message-text')}
                                                </div>
                                            </div>
                                            <PostForm
                                                onlineCondolence={onlineCondolence}
                                                postType={PostType.MessageWithAudioLink}
                                                handleFormVisibility={() => setIsFormShown(false)}
                                                onPostCreated={addNewPost}
                                            />
                                        </div>
                                    </Tab>
                                    <Tab label={t('create-video-message-tab')}
                                         isEnabled={onlineCondolence.enableVideoMessage}>
                                        <div className={'cr-create-video-message'}>
                                            <div className={'cr-tab-header cr-text-center'}>
                                                <div className={'cr-tab-title cr-text-lg cr-text-primary cr-mb-sm'}>
                                                    {t('create-video-message-title')}
                                                </div>
                                                <div className={'cr-tab-description cr-text-primary'}>
                                                    {t('create-video-message-text')}
                                                </div>
                                            </div>
                                            <PostForm
                                                onlineCondolence={onlineCondolence}
                                                postType={PostType.MessageWithVideoLink}
                                                handleFormVisibility={() => setIsFormShown(false)}
                                                onPostCreated={addNewPost}
                                            />
                                        </div>
                                    </Tab>
                                </Tabs>
                                :
                                (buttonLayout === ButtonLayout.Wide) &&
                                    <button
                                        className='cr-create-post cr-pill cr-bg-alert cr-text-white cr-text-base'
                                        onClick={() => setIsFormShown(!isFormShown)}
                                    >
                                        {t('create-post')}
                                    </button>
                            }
                        </div>
                    }
                    {showPosts &&
                        <PaginatedCollection
                            id={'postCollection'}
                            items={sortedPosts}
                            renderItems={{
                                [ViewMode.GridView]: renderCard,
                                [ViewMode.ListView]: renderListItem,
                                [ViewMode.ListLeftToRightView]: renderListLtrItem,
                            }}
                            itemsPerPage={6}
                            itemsPerRow={3}
                            itemsPerRowTablet={2}
                            itemsPerRowMobile={1}
                            viewMode={viewMode}
                            rowGapClass={(viewMode === ViewMode.ListView) ? 'cr-row-gap-lg' : undefined }
                        />
                    }
                </div>
            </div>
        </div>
    ) : null;
}

export default PostCollection;
